import React, { FC, useState, useCallback, useRef, useEffect } from 'react';
import {
  Upload,
  File,
  X,
  Eye,
  FileText,
  Image,
  FileAudio,
  Trash2,
  AlertCircle,
  CheckCircle,
  Loader,
  MapPin,
  Camera,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { extractPhotoMetadata, getMetadataSummary, type PhotoMetadata } from '../../services/metadataService';
import { locationService, type LocationCoordinates } from '../../utils/locationService';
import { useLanguage } from '../../contexts/LanguageContext';

interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'failed';
  progress?: number;
  error?: string;
  metadata?: PhotoMetadata;
  metadataSummary?: string;
  metadataExtractionTime?: number;
  location?: LocationCoordinates;
}

interface FileUploadWithPreviewProps {
  onFileUpload: (files: File[], metadata?: { [fileName: string]: any }) => Promise<void>;
  acceptedTypes?: string;
  maxFileSize?: number; // in MB
  maxFiles?: number;
  className?: string;
  disabled?: boolean;
  resetKey?: string | number; // Add reset key to trigger component reset
  enableMetadataExtraction?: boolean; // New prop to enable/disable metadata extraction
}

export const FileUploadWithPreview: FC<FileUploadWithPreviewProps> = ({
  onFileUpload,
  acceptedTypes = "image/*,application/pdf,.doc,.docx,.txt",
  maxFileSize = 10, // 10MB default
  maxFiles = 5,
  className = "",
  disabled = false,
  resetKey,
  enableMetadataExtraction = true // Enable by default for images
}) => {
  const { t } = useLanguage();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset component state when resetKey changes
  useEffect(() => {
    console.log('🔄 FileUploadWithPreview reset triggered:', { resetKey });
    setUploadedFiles([]);
    setIsUploading(false);
    setDragActive(false);
    setPreviewFile(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetKey]);
    
  const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="w-5 h-5 text-primary" />;
    if (type.startsWith('audio/')) return <FileAudio className="w-5 h-5 text-green-500" />;
    if (type.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize * 1024 * 1024) {
      return t('file_upload.file_size_exceeds', { maxSize: maxFileSize });
    }

    // Check file type
    const acceptedTypesList = acceptedTypes.split(',').map(type => type.trim());
    const isValidType = acceptedTypesList.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      if (type.includes('*')) {
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType);
      }
      return file.type === type;
    });

    if (!isValidType) {
      return t('file_upload.file_type_not_supported', { types: acceptedTypes });
    }

    return null;
  };

  const createFilePreview = async (file: File): Promise<string | undefined> => {
    if (file.type.startsWith('image/')) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });
    }
    return undefined;
  };

  const extractImageMetadata = async (file: File): Promise<{
    metadata?: PhotoMetadata;
    metadataSummary?: string;
    extractionTime: number;
    location?: LocationCoordinates;
  }> => {
    const startTime = performance.now();

    try {
      if (!enableMetadataExtraction || !file.type.startsWith('image/')) {
        return { extractionTime: 0 };
      }

      console.log('📊 Extracting metadata from uploaded image:', file.name);

      // Extract metadata using the existing service
      const result = await extractPhotoMetadata(file);
      const metadataSummary = getMetadataSummary(result.metadata);

      // Get location for the image
      let location: LocationCoordinates | undefined;

      if (result.metadata.gpsCoordinates) {
        // Use GPS coordinates from metadata
        location = {
          latitude: result.metadata.gpsCoordinates.latitude,
          longitude: result.metadata.gpsCoordinates.longitude,
          source: 'metadata'
        };
        console.log('📍 Using GPS coordinates from image metadata');
      } else {
        // Get current location as fallback
        try {
          const locationResult = await locationService.getCurrentLocation({
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 30000
          });

          if (locationResult.location) {
            location = locationResult.location;
            console.log('📍 Using current location for image');
          }
        } catch (locationError) {
          console.warn('⚠️ Could not get location for image:', locationError);
        }
      }

      const extractionTime = performance.now() - startTime;

      console.log('✅ Image metadata extraction completed:', {
        fileName: file.name,
        processingTime: `${extractionTime.toFixed(2)}ms`,
        summary: metadataSummary,
        hasGPS: !!result.metadata.gpsCoordinates,
        dataQuality: result.metadata.dataQuality
      });

      return {
        metadata: result.metadata,
        metadataSummary,
        extractionTime,
        location
      };

    } catch (error) {
      const extractionTime = performance.now() - startTime;
      console.warn('⚠️ Metadata extraction failed for', file.name, ':', error);

      return {
        extractionTime,
        metadataSummary: t('file_upload.metadata_extraction_failed')
      };
    }
  };

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;

    const newFiles: UploadedFile[] = [];
    const validFiles: File[] = [];
    const extractedMetadata: { [fileName: string]: any } = {};

    // Check total file limit
    if (uploadedFiles.length + files.length > maxFiles) {
      alert(t('file_upload.max_files_exceeded', { maxFiles }));
      return;
    }

    // Process each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validationError = validateFile(file);

      if (validationError) {
        alert(`${file.name}: ${validationError}`);
        continue;
      }

      const fileId = generateFileId();
      const preview = await createFilePreview(file);

      // Extract metadata for images if enabled
      let metadata: PhotoMetadata | undefined;
      let metadataSummary: string | undefined;
      let metadataExtractionTime: number | undefined;
      let location: LocationCoordinates | undefined;

      if (enableMetadataExtraction && file.type.startsWith('image/')) {
        const extractionResult = await extractImageMetadata(file);
        metadata = extractionResult.metadata;
        metadataSummary = extractionResult.metadataSummary;
        metadataExtractionTime = extractionResult.extractionTime;
        location = extractionResult.location;

        // Store metadata for the upload callback
        if (metadata) {
          extractedMetadata[file.name] = {
            metadata,
            metadataSummary,
            extractionTime: metadataExtractionTime,
            location
          };
        }
      }

      newFiles.push({
        id: fileId,
        file,
        preview,
        status: 'pending',
        metadata,
        metadataSummary,
        metadataExtractionTime,
        location
      });

      validFiles.push(file);
    }

    if (newFiles.length === 0) return;

    // Add files to state
    console.log('📁 FileUploadWithPreview: Adding files to state:', {
        newFilesCount: newFiles.length,
        newFiles: newFiles.map(f => ({
          id: f.id,
          name: f.file.name,
          status: f.status,
          hasMetadata: !!f.metadata,
          metadataQuality: f.metadata?.dataQuality
        })),
        validFilesCount: validFiles.length,
        currentFilesCount: uploadedFiles.length,
        extractedMetadataCount: Object.keys(extractedMetadata).length
    });

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // DEBUG: Verify state update
    setTimeout(() => {
        console.log('📁 FileUploadWithPreview: State after update - should have', uploadedFiles.length + newFiles.length, 'files');
    }, 100);

    // Start upload process
    if (validFiles.length > 0) {
      setIsUploading(true);
      try {
        // Update status to uploading
        setUploadedFiles(prev =>
          prev.map(f =>
            newFiles.find(nf => nf.id === f.id)
              ? { ...f, status: 'uploading' as const }
              : f
          )
        );

        console.log('📁 FileUploadWithPreview: Calling onFileUpload with validFiles:', {
            validFilesCount: validFiles.length,
            validFiles: validFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
            extractedMetadataCount: Object.keys(extractedMetadata).length
        });

        // Pass files and extracted metadata to the upload handler
        await onFileUpload(validFiles, Object.keys(extractedMetadata).length > 0 ? extractedMetadata : undefined);
        console.log('📁 FileUploadWithPreview: onFileUpload completed successfully');

        // Update status to uploaded
        setUploadedFiles(prev =>
          prev.map(f =>
            newFiles.find(nf => nf.id === f.id)
              ? { ...f, status: 'uploaded' as const }
              : f
          )
        );
      } catch (error) {
        // Update status to failed
        setUploadedFiles(prev =>
          prev.map(f =>
            newFiles.find(nf => nf.id === f.id)
              ? { ...f, status: 'failed' as const, error: error instanceof Error ? error.message : 'Upload failed' }
              : f
          )
        );
      } finally {
        setIsUploading(false);
      }
    }
  }, [uploadedFiles.length, maxFiles, onFileUpload, disabled, enableMetadataExtraction]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [handleFileSelect]);

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const retryUpload = async (fileId: string) => {
    const fileToRetry = uploadedFiles.find(f => f.id === fileId);
    if (!fileToRetry) return;

    setUploadedFiles(prev =>
      prev.map(f =>
        f.id === fileId
          ? { ...f, status: 'uploading' as const, error: undefined }
          : f
      )
    );

    try {
      await onFileUpload([fileToRetry.file]);
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'uploaded' as const }
            : f
        )
      );
    } catch (error) {
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'failed' as const, error: error instanceof Error ? error.message : 'Upload failed' }
            : f
        )
      );
    }
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading':
        return <Loader className="w-4 h-4 text-primary animate-spin" />;
      case 'uploaded':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-primary bg-primary/10 dark:bg-primary/20'
            : disabled
            ? 'border-gray-200 bg-background dark:bg-gray-800'
            : 'border-input hover:border-gray-400 dark:border-gray-600'
        }`}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes}
          onChange={(e) => handleFileSelect(e.target.files)}
          disabled={disabled || isUploading}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />

        <div className="space-y-2">
          <Upload className={`w-8 h-8 mx-auto ${disabled ? 'text-gray-400' : 'text-gray-500'}`} />
          <div>
            <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
              {dragActive
                ? t('file_upload.drop_files')
                : t('file_upload.drag_drop_files')
              }
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('file_upload.max_files_info', { maxFiles, maxFileSize })}
            </p>
          </div>
        </div>

        {isUploading && (
          <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 flex items-center justify-center rounded-lg">
            <div className="flex items-center space-x-2">
              <Loader className="w-5 h-5 animate-spin text-primary" />
              <span className="text-sm text-gray-600">{t('file_upload.uploading')}</span>
            </div>
          </div>
        )}
      </div>

      {/* File List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('file_upload.uploaded_files', { count: uploadedFiles.length })}
          </h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {uploadedFiles.map((uploadedFile) => (
              <div
                key={uploadedFile.id}
                className="flex items-center space-x-3 p-3 bg-background dark:bg-gray-800 rounded-lg border"
              >
                {/* File Icon */}
                <div className="flex-shrink-0">
                  {getFileIcon(uploadedFile.file.type)}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {uploadedFile.file.name}
                    </p>
                    {getStatusIcon(uploadedFile.status)}
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span>{formatFileSize(uploadedFile.file.size)}</span>
                    <span>•</span>
                    <span className="capitalize">{uploadedFile.status}</span>
                    {uploadedFile.error && (
                      <>
                        <span>•</span>
                        <span className="text-red-500">{uploadedFile.error}</span>
                      </>
                    )}
                  </div>

                  {/* Metadata Information */}
                  {uploadedFile.metadataSummary && uploadedFile.file.type.startsWith('image/') && (
                    <div className="mt-2 p-2 bg-primary/10 dark:bg-primary/20 rounded text-xs">
                      <div className="flex items-center space-x-1 text-primary dark:text-primary mb-1">
                        <Camera className="w-3 h-3" />
                        <span className="font-medium">{t('file_upload.photo_metadata')}</span>
                        {uploadedFile.metadataExtractionTime && (
                          <span className="text-xs text-primary">
                            ({uploadedFile.metadataExtractionTime.toFixed(0)}ms)
                          </span>
                        )}
                      </div>
                      <div className="text-primary dark:text-primary">
                        {uploadedFile.metadataSummary}
                      </div>

                      {/* Additional metadata details */}
                      {uploadedFile.metadata && (
                        <div className="mt-1 space-y-1">
                          {uploadedFile.metadata.gpsCoordinates && (
                            <div className="flex items-center space-x-1 text-xs text-green-600 dark:text-green-400">
                              <MapPin className="w-3 h-3" />
                              <span>
                                {uploadedFile.metadata.gpsCoordinates.latitude.toFixed(6)}, {uploadedFile.metadata.gpsCoordinates.longitude.toFixed(6)}
                              </span>
                            </div>
                          )}

                          {uploadedFile.metadata.cameraMake && uploadedFile.metadata.cameraModel && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              📷 {uploadedFile.metadata.cameraMake} {uploadedFile.metadata.cameraModel}
                            </div>
                          )}

                          {uploadedFile.metadata.dateTimeOriginal && (
                            <div className="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400">
                              <Clock className="w-3 h-3" />
                              <span>
                                {new Date(uploadedFile.metadata.dateTimeOriginal).toLocaleDateString()} {new Date(uploadedFile.metadata.dateTimeOriginal).toLocaleTimeString()}
                              </span>
                            </div>
                          )}

                          {uploadedFile.metadata.isEdited && (
                            <div className="flex items-center space-x-1 text-xs text-amber-600 dark:text-amber-400">
                              <AlertTriangle className="w-3 h-3" />
                              <span>{t('file_upload.edited', { indicator: uploadedFile.metadata.editIndicators?.[0] || t('file_upload.edited_unknown') })}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-1">
                  {uploadedFile.preview && (
                    <button
                      onClick={() => setPreviewFile(uploadedFile)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded"
                      title={t('file_upload.preview')}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}

                  {uploadedFile.status === 'failed' && (
                    <button
                      onClick={() => retryUpload(uploadedFile.id)}
                      className="p-1 text-primary hover:text-primary rounded"
                      title={t('file_upload.retry')}
                    >
                      <Upload className="w-4 h-4" />
                    </button>
                  )}

                  <button
                    onClick={() => removeFile(uploadedFile.id)}
                    className="p-1 text-red-400 hover:text-red-600 rounded"
                    title={t('file_upload.remove')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewFile && previewFile.preview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[80]">
          <div className="relative max-w-4xl max-h-[90vh] m-4">
            <button
              onClick={() => setPreviewFile(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
            >
              <X className="w-5 h-5" />
            </button>

            <img
              src={previewFile.preview}
              alt={previewFile.file.name}
              className="max-w-full max-h-full object-contain rounded-lg"
            />

            <div className="absolute bottom-4 left-4 right-4 text-center">
              <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
                <p className="font-medium">{previewFile.file.name}</p>
                <p className="text-sm opacity-75">{formatFileSize(previewFile.file.size)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};